import { getSessionForAuthenticatedRequest } from '../auth.ts';
import { applyTenantRequestHeaders } from '../tenantRequestContext';
import { fetchPersistedCsvTrades, resetPersistedCsvTradesCache } from './persistedCsvTradeRead';
import { brokerOptions } from './csvBrokers';
import { getClearAccountIdentity, IMPORTED_TRADES_UPDATED_EVENT, isCsvImportedTradeForClear, normalizeClearBroker, removeImportedTradesForAccount } from '../../utils/importedTradesStorage';

export type CsvImportPersistRequest = {
  rawRows: any[];
  filename?: string;
  timeZone?: string;
  sourceBroker: string;
  accountId?: string;
  /** Internal ID of the authenticated user's saved CSV source-account row. */
  sourceAccountId?: string;
  accountTag?: string;
  sourceAccountRef?: string;
  createAccount?: boolean;
  accountCreationId?: string;
  brokerMode?: 'auto' | 'manual';
  clientAcceptedCount?: number;
  clientDuplicateCount?: number;
  tags?: string[];
  notes?: string;
};

export type CsvImportPersistInput = {
  rawRows: any[];
  filename?: string | null;
  timeZone?: string | null;
  sourceBroker?: string | null;
  brokerId?: string | null;
  accountId?: string | null;
  providerAccountId?: string | null;
  sourceAccountId?: string | null;
  accountTag?: string | null;
  sourceAccountRef?: string | null;
  createAccount?: boolean;
  accountCreationId?: string | null;
  brokerMode?: 'auto' | 'manual' | null;
  clientAcceptedCount?: number | string | null;
  clientDuplicateCount?: number | string | null;
  tags?: string[] | string | null;
  notes?: string | null;
};

type PersistRemote = (requestBody: CsvImportPersistRequest) => Promise<unknown>;

export function shouldPersistCsvImportToBackend(counts: {
  duplicateExecutionCount?: number;
  incompleteLifecycleCount?: number;
  rejectedOverlapLifecycleCount?: number;
}): boolean {
  return Number(counts.duplicateExecutionCount || 0) === 0
    && Number(counts.incompleteLifecycleCount || 0) === 0
    && Number(counts.rejectedOverlapLifecycleCount || 0) === 0;
}

export function shouldResolveCsvPersistenceAccount(counts: {
  incomingTradeCount?: number;
  duplicateExecutionCount?: number;
}): boolean {
  return Number(counts.incomingTradeCount || 0) > 0
    || Number(counts.duplicateExecutionCount || 0) > 0;
}

export function resolveExactFileDuplicateTradeCount(
  currentDuplicateTradeCount: unknown,
  persistedAcceptedTradeCount: unknown,
): number {
  const toCount = (value: unknown) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
  };
  return Math.max(1, toCount(currentDuplicateTradeCount), toCount(persistedAcceptedTradeCount));
}

export function isCsvImportOutcomeConfirmed(result: {
  localSaveFailed: boolean;
  failureMessage?: string;
  summary?: unknown;
}): boolean {
  return !result.localSaveFailed && !result.failureMessage?.trim() && result.summary != null;
}

export function saveAcceptedImportedTrades<T>(
  existingTrades: T[],
  acceptedIncoming: T[],
  save: (trades: T[]) => unknown,
): boolean {
  if (!Array.isArray(acceptedIncoming) || acceptedIncoming.length === 0) return false;
  return save([...existingTrades, ...acceptedIncoming]) !== false;
}

function getBase(): string {
  const envBase = String((import.meta as any).env?.VITE_API_BASE || '').trim();
  return envBase.replace(/\/+$/, '');
}

function buildUrl(path: string): string {
  const base = getBase();
  return base ? `${base}${path}` : path;
}


function toTrimmedString(value: unknown): string {
  return String(value ?? '').trim();
}

function toFiniteNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : undefined;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => toTrimmedString(item)).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(/[;,|]/g)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

export async function buildCsvImportAuthHeaders(): Promise<Record<string, string>> {
  const session = await getSessionForAuthenticatedRequest({ reason: 'csv_import' });
  const accessToken = toTrimmedString(session?.access_token);

  if (!accessToken) {
    throw new Error('missing_access_token');
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };

  return applyTenantRequestHeaders(headers);
}

async function buildAuthHeaders(): Promise<Record<string, string>> {
  return buildCsvImportAuthHeaders();
}

export function getCsvImportErrorMessage(value: unknown, fallback = 'CSV import failed.'): string {
  const extract = (candidate: unknown, depth: number): string => {
    if (depth > 3 || candidate == null) return '';
    if (candidate instanceof Error) {
      const message = candidate.message.trim();
      const errorStatus = Number((candidate as Error & { status?: unknown }).status);
      if (errorStatus === 401) return 'Your session could not be verified. Sign in again and retry the import.';
      if (errorStatus === 403) return 'You do not have access to import into the selected trading account.';
      if (errorStatus >= 500) return 'The import service encountered an error. The import status may be uncertain; verify the trades before trying the CSV again.';
      if (candidate instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(message)) {
        return 'Could not connect to the import service. Check your connection and try again.';
      }
      return message;
    }
    if (typeof candidate === 'string') {
      const raw = candidate.trim();
      if (!raw) return '';
      if (/^[{[]/.test(raw)) {
        try { return extract(JSON.parse(raw), depth + 1); } catch {}
      }
      return raw;
    }
    if (typeof candidate !== 'object') return '';

    const response = candidate as Record<string, unknown>;
    const code = String(response.code || response.failureCode || (typeof response.error === 'string' ? response.error : '')).trim().toUpperCase();
    if (code === 'CSV_ACCOUNT_SCOPE_CONFLICT') {
      const detail = extract(response.detail, depth + 1);
      if (/account identifier|provider account id|account reference|selected .*account|csv account/i.test(detail)
          && /does not match|different saved account/i.test(detail)) {
        return 'These trades belong to a different trading account. Select the account that matches the CSV and try again.';
      }
      if (/already connected through broker sync/i.test(detail)) {
        return 'This broker account is already connected through Broker Sync. Select the connected account instead of importing it as a separate CSV account.';
      }
      return 'The selected CSV account could not be confirmed. Reload the account list, select the correct account, and try again.';
    }
    if (code === 'AMBIGUOUS_EXECUTION_ORDER') {
      const diagnostic = Array.isArray(response.rejectionDiagnostics)
        ? response.rejectionDiagnostics.find((item) => item && typeof item === 'object') as Record<string, unknown> | undefined
        : undefined;
      const actionable = [diagnostic?.explanation, diagnostic?.suggestedAction]
        .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        .join(' ');
      if (actionable) return actionable;
      return 'Some executions share timestamps and could produce different trade boundaries. Include verified broker sequence data or correct the timestamps before importing.';
    }
    for (const key of ['error', 'message', 'description']) {
      const message = extract(response[key], depth + 1);
      if (message) return message;
    }
    return '';
  };

  const httpStatus = Number((value as { httpStatus?: unknown; status?: unknown } | null)?.httpStatus
    ?? (value as { status?: unknown } | null)?.status);
  if (httpStatus === 401) return 'Your session could not be verified. Sign in again and retry the import.';
  if (httpStatus === 403) return 'You do not have access to import into the selected trading account.';
  if (httpStatus >= 500) return 'The import service encountered an error. The import status may be uncertain; verify the trades before trying the CSV again.';

  const extracted = extract(value, 0);
  const cleaned = extracted
    .replace(/<svg\b[^>]*>[\s\S]*?<\/svg\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\bsvg\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;!?])/g, '$1')
    .trim();
  return cleaned || fallback;
}

async function fetchJson(url: string, init?: RequestInit): Promise<any> {
  let response: Response;
  try {
    response = await fetch(url, init);
  } catch (error) {
    throw new Error(getCsvImportErrorMessage(error, 'Could not connect to the import service. Check your connection and try again.'));
  }
  const text = await response.text();

  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { raw: text };
  }

  if (!response.ok) {
    const message = getCsvImportErrorMessage({ ...(data && typeof data === 'object' ? data : {}), httpStatus: response.status }, `Import service returned HTTP ${response.status}.`);
    const error = new Error(message) as Error & { code?: string; details?: unknown; status?: number };
    error.code = String(data?.code || '').toUpperCase() || undefined;
    error.details = data?.details;
    error.status = response.status;
    throw error;
  }

  return data;
}

export function buildCsvImportPersistRequest(input: CsvImportPersistInput): CsvImportPersistRequest {
  const rawRows = Array.isArray(input.rawRows) ? input.rawRows : [];
  const sourceBroker = toTrimmedString(input.sourceBroker ?? input.brokerId ?? 'csv_generic') || 'csv_generic';
  const filename = toTrimmedString(input.filename);
  const timeZone = toTrimmedString(input.timeZone);
  const accountId = toTrimmedString(input.accountId ?? input.providerAccountId);
  const sourceAccountId = toTrimmedString(input.sourceAccountId);
  const accountTag = toTrimmedString(input.accountTag);
  const sourceAccountRef = toTrimmedString(input.sourceAccountRef);
  const createAccount = input.createAccount === true;
  const accountCreationId = toTrimmedString(input.accountCreationId);
  const brokerMode = input.brokerMode === 'manual' ? 'manual' : 'auto';
  const clientAcceptedCount = toFiniteNumber(input.clientAcceptedCount);
  const clientDuplicateCount = toFiniteNumber(input.clientDuplicateCount);
  const tags = toStringArray(input.tags);
  const notes = toTrimmedString(input.notes);

  const requestBody: CsvImportPersistRequest = {
    rawRows,
    sourceBroker,
  };

  if (sourceAccountId) requestBody.sourceAccountId = sourceAccountId;
  if (filename) requestBody.filename = filename;
  if (timeZone) requestBody.timeZone = timeZone;
  if (accountId) requestBody.accountId = accountId;
  if (accountTag) requestBody.accountTag = accountTag;
  if (sourceAccountRef) requestBody.sourceAccountRef = sourceAccountRef;
  if (input.brokerMode === 'manual' || createAccount) requestBody.brokerMode = brokerMode;
  if (createAccount) {
    requestBody.createAccount = true;
    if (accountCreationId) requestBody.accountCreationId = accountCreationId;
  }
  if (clientAcceptedCount != null) requestBody.clientAcceptedCount = clientAcceptedCount;
  if (clientDuplicateCount != null) requestBody.clientDuplicateCount = clientDuplicateCount;
  if (tags.length > 0) requestBody.tags = tags;
  if (notes) requestBody.notes = notes;

  return requestBody;
}

export type CsvSourceAccountOption = {
  id: string;
  sourceBroker: string;
  accountName: string;
  sourceAccountRef?: string;
  providerAccountId?: string | null;
  status?: string;
  tradeCount?: number;
  executionCount?: number;
  importBatchCount?: number;
  isDefault?: boolean;
  lastImportAt?: string | null;
};

export function isActiveCsvSourceAccount(account: CsvSourceAccountOption | null | undefined): account is CsvSourceAccountOption & { id: string; sourceAccountRef: string; status: 'active' } {
  return Boolean(
    account
    && String(account.id || '').trim()
    && String(account.sourceAccountRef || '').trim()
    && String(account.status || '').trim().toLowerCase() === 'active'
  );
}

export function formatCsvAccountLastImport(value?: string | null): string {
  if (!value) return 'Never imported';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString() : 'Never imported';
}

const supportedCsvBrokerIds = new Set(
  brokerOptions.filter(({ id }) => id !== 'csv_generic' && id !== 'other').map(({ id }) => id),
);

/** CSV account labels are not provider identities; only explicit ID fields count. */
export function getCsvProviderAccountIds(rawRows: any[], trades: any[] = []): string[] {
  const explicitRowIds = (Array.isArray(rawRows) ? rawRows : []).flatMap((row) => {
    const normalized = Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [
      key.toLowerCase().replace(/[^a-z0-9]/g, ''), value,
    ]));
    return ['provideraccountid', 'accountid', 'accountnumber']
      .map((key) => toTrimmedString(normalized[key]))
      .filter(Boolean);
  });
  const explicitTradeIds = (Array.isArray(trades) ? trades : [])
    .map((trade) => toTrimmedString(trade?.providerAccountId))
    .filter(Boolean);
  return [...new Set([...explicitRowIds, ...explicitTradeIds])];
}

export function buildCsvSourceAccountResolutionInput(input: {
  sourceBroker: string;
  accountName: string;
  sourceAccountId?: string;
  rawRows: any[];
  trades?: any[];
}): {
  sourceBroker: string;
  accountTag: string;
  accountId?: string;
  sourceAccountId?: string;
  rawRows: any[];
} {
  const sourceBroker = toTrimmedString(input.sourceBroker).toLowerCase();
  const accountName = toTrimmedString(input.accountName);
  const sourceAccountId = toTrimmedString(input.sourceAccountId);
  if (!supportedCsvBrokerIds.has(sourceBroker)) {
    throw new Error('Select a supported broker before importing this CSV.');
  }
  if (!accountName) {
    throw new Error('Select a saved broker account or explicitly create a named account before importing.');
  }

  const providerAccountIds = getCsvProviderAccountIds(input.rawRows, input.trades || []);
  if (providerAccountIds.length > 1) {
    throw new Error('This CSV contains multiple broker account IDs. Import each account separately.');
  }

  return {
    sourceBroker,
    accountTag: accountName,
    accountId: providerAccountIds[0],
    sourceAccountId: sourceAccountId || undefined,
    rawRows: Array.isArray(input.rawRows) ? input.rawRows : [],
  };
}

export async function renameCsvSourceAccount(accountId: string, accountName: string): Promise<CsvSourceAccountOption> {
  const result = await fetchJson(buildUrl(`/api/trades/imports/csv/accounts/${encodeURIComponent(accountId)}`), {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
    body: JSON.stringify({ accountName }),
  });
  return result?.account as CsvSourceAccountOption;
}

export async function deleteCsvSourceAccount(account: CsvSourceAccountOption): Promise<any> {
  const accountId = toTrimmedString(account?.id);
  const sourceBroker = toTrimmedString(account?.sourceBroker);
  const sourceAccountRef = toTrimmedString(account?.sourceAccountRef);
  if (!accountId || !sourceBroker || !sourceAccountRef || account?.status === 'archived') {
    throw new Error('Select a valid active saved account before deleting it.');
  }
  const result = await fetchJson(buildUrl(`/api/trades/imports/csv/accounts/${encodeURIComponent(accountId)}/with-imported-data`), {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
    body: JSON.stringify({ sourceBroker, sourceAccountRef }),
  });
  if (result?.ok !== true || result?.account?.sourceAccountId !== accountId || result?.accountDeleted !== true) {
    throw new Error('The saved account and its imported data were not confirmed as deleted.');
  }
  const local = removeImportedTradesForAccount({
    sourceBroker,
    sourceAccountRef,
    sourceAccountId: accountId,
    emitEvent: false,
  });
  resetPersistedCsvTradesCache();
  window.dispatchEvent(new CustomEvent('trade-proof-csv-imports-cleared', { detail: {
    localClearSucceeded: local.ok,
    sourceBroker,
    sourceAccountRef,
    sourceAccountId: accountId,
  } }));
  window.dispatchEvent(new CustomEvent('trade-proof-csv-account-cleared', {
    detail: { accountId, deletedExecutionCount: Number(result.deletedExecutionCount) || 0, accountDeleted: true },
  }));
  window.dispatchEvent(new Event(IMPORTED_TRADES_UPDATED_EVENT));
  return { ...result, localClearSucceeded: local.ok };
}

export async function archiveCsvSourceAccount(accountId: string): Promise<void> {
  const result = await fetchJson(buildUrl(`/api/trades/imports/csv/accounts/${encodeURIComponent(accountId)}/archive`), {
    method: 'POST', headers: await buildAuthHeaders(),
  });
  if (result?.archived !== true || result?.account?.id !== accountId || result?.account?.status !== 'archived') {
    throw new Error('The account archive was not confirmed by the server.');
  }
}

export async function setDefaultCsvSourceAccount(accountId: string): Promise<void> {
  const result = await fetchJson(buildUrl('/api/trades/imports/csv/accounts/default'), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
    body: JSON.stringify({ accountId }),
  });
  if (result?.isDefault !== true || result?.accountId !== accountId) {
    throw new Error('The default account change was not confirmed by the server.');
  }
}

export async function clearCsvSourceAccountTrades(account: CsvSourceAccountOption, confirmLinkedContent = false): Promise<any> {
  const sourceAccountId = toTrimmedString(account?.id);
  const sourceBroker = toTrimmedString(account?.sourceBroker);
  const sourceAccountRef = toTrimmedString(account?.sourceAccountRef);
  if (!sourceAccountId || !sourceBroker || !sourceAccountRef || account?.status === 'archived') {
    throw new Error('Select a valid active saved account before clearing imported trades.');
  }
  const result = await fetchJson(buildUrl('/api/trades/imports/csv/clear-account'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
    body: JSON.stringify({ sourceAccountId, sourceBroker, sourceAccountRef, confirmLinkedContent }),
  });
  if (result?.ok !== true || result?.account?.sourceAccountId !== sourceAccountId) {
    throw new Error('The account-scoped clear was not confirmed by the server.');
  }
  return result;
}

/** Reconcile stale local CSV rows only after a fresh complete server read proves the account is empty. */
export async function reconcileClearedCsvSourceAccountCache(account: CsvSourceAccountOption): Promise<{ removedCount: number }> {
  const accountId = toTrimmedString(account?.id);
  const sourceBroker = toTrimmedString(account?.sourceBroker);
  const sourceAccountRef = toTrimmedString(account?.sourceAccountRef);
  if (!accountId || !sourceBroker || !sourceAccountRef) throw new Error('cleared_account_identity_unavailable');

  resetPersistedCsvTradesCache();
  const pageSize = 500;
  let offset = 0;
  let page: any[];
  do {
    page = await fetchPersistedCsvTrades({ limit: pageSize, offset });
    if (page.some((trade) => {
      const identity = getClearAccountIdentity(trade);
      return isCsvImportedTradeForClear(trade)
        && identity?.broker === normalizeClearBroker(sourceBroker)
        && identity.ref === sourceAccountRef;
    })) throw new Error('cleared_account_still_has_persisted_trades');
    offset += page.length;
    if (offset > 500_000) throw new Error('cleared_account_authoritative_read_incomplete');
  } while (page.length === pageSize);

  const local = removeImportedTradesForAccount({
    sourceBroker,
    sourceAccountRef,
    sourceAccountId: accountId,
    allowVerifiedReferenceMatch: true,
    emitEvent: false,
  });
  if (!local.ok) throw new Error('cleared_account_local_cache_write_failed');
  resetPersistedCsvTradesCache();
  return { removedCount: local.removed.length };
}

export function resolveCsvPersistenceBroker(options: {
  detectedBroker?: string | null;
  selectedSourceAccountId?: string | null;
  savedAccounts?: CsvSourceAccountOption[];
}): string {
  const detected = toTrimmedString(options.detectedBroker).toLowerCase();
  if (detected && detected !== 'auto' && detected !== 'csv_generic') return detected;
  const selectedId = toTrimmedString(options.selectedSourceAccountId);
  const selected = (options.savedAccounts || []).find((account) => account.id === selectedId);
  return toTrimmedString(selected?.sourceBroker).toLowerCase() || detected || 'csv_generic';
}

export async function listCsvSourceAccounts(sourceBroker?: string): Promise<CsvSourceAccountOption[]> {
  const query = sourceBroker && sourceBroker !== 'auto'
    ? `?sourceBroker=${encodeURIComponent(sourceBroker)}`
    : '';
  const result = await fetchJson(buildUrl(`/api/trades/imports/csv/accounts${query}`), {
    method: 'GET', headers: await buildAuthHeaders(),
  });
  return Array.isArray(result?.accounts) ? result.accounts : [];
}

export async function resolveCsvSourceAccount(input: {
  sourceBroker: string;
  accountTag?: string;
  accountId?: string;
  sourceAccountId?: string;
  rawRows: any[];
}): Promise<{ mode: string; account: CsvSourceAccountOption | null }> {
  const result = await fetchJson(buildUrl('/api/trades/imports/csv/accounts/resolve'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await buildAuthHeaders()) },
    body: JSON.stringify(input),
  });
  return { mode: String(result?.mode || ''), account: result?.account || null };
}

export function requireResolvedCsvSourceAccount(
  result: { mode: string; account: CsvSourceAccountOption | null },
  expectedSourceBroker: string,
): CsvSourceAccountOption {
  const account = result?.account;
  if (result?.mode !== 'resolved' || !account?.id || !account.sourceAccountRef
      || account.sourceBroker !== expectedSourceBroker) {
    throw new Error('A stable broker account could not be confirmed. No trades were saved.');
  }
  return account;
}

export async function persistCsvImportToBackend(
  requestBody: CsvImportPersistRequest,
  signal?: AbortSignal
): Promise<unknown> {
  return fetchJson(buildUrl('/api/trades/imports/csv/persist'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(await buildAuthHeaders()),
    },
    signal,
    body: JSON.stringify(requestBody),
  });
}

export type CsvImportVerificationOptions = {
  maxAttempts?: number;
  retryDelayMs?: number;
  wait?: (milliseconds: number) => Promise<void>;
};

export async function verifyPersistedCsvImport(remoteResult: any, options: CsvImportVerificationOptions = {}): Promise<boolean> {
  const responseStatus = String(remoteResult?.status || '').trim().toLowerCase();
  const responseMode = String(remoteResult?.mode || '').trim().toLowerCase();
  if (responseStatus === 'preview_only' || responseMode === 'isolated_preview') {
    return false;
  }
  if (responseStatus === 'duplicate_exact_file'
      && !['completed', 'partial'].includes(String(remoteResult?.batchStatus || '').trim().toLowerCase())) {
    return false;
  }
  const batchId = String(remoteResult?.importBatchId || '').trim();
  const expectedTrades = Number(remoteResult?.batchCounts?.acceptedTradeCount);
  const expectedExecutions = Number(remoteResult?.batchCounts?.acceptedExecutionCount);
  if (!batchId || !Number.isFinite(expectedTrades) || expectedTrades <= 0
      || !Number.isFinite(expectedExecutions) || expectedExecutions <= 0) {
    return false;
  }

  const maxAttempts = Math.max(1, Math.min(5, Math.floor(options.maxAttempts ?? 4)));
  const retryDelayMs = Math.max(0, Math.floor(options.retryDelayMs ?? 400));
  const wait = options.wait ?? ((milliseconds: number) => new Promise<void>((resolve) => setTimeout(resolve, milliseconds)));
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let persistedTradeCount = 0;
    let persistedExecutionCount = 0;
    let offset = 0;
    while (persistedTradeCount < expectedTrades || persistedExecutionCount < expectedExecutions) {
      const trades = await fetchPersistedCsvTrades({ limit: 500, offset, importBatchId: batchId });
      persistedTradeCount += trades.length;
      persistedExecutionCount += trades.reduce((count, trade) => count + (Array.isArray(trade?.executionsList) ? trade.executionsList.length : 0), 0);
      if (trades.length < 500) break;
      offset += trades.length;
    }
    if (persistedTradeCount >= expectedTrades && persistedExecutionCount >= expectedExecutions) return true;
    if (attempt + 1 < maxAttempts) await wait(retryDelayMs * (attempt + 1));
  }
  return false;
}

/** Read-only recovery eligibility; failed batches cannot be retried as if they persisted. */
export function isCsvImportVerificationRetryable(remoteResult: unknown): boolean {
  if (!remoteResult || typeof remoteResult !== 'object') return false;
  const status = String((remoteResult as any).status || '').trim().toLowerCase();
  const mode = String((remoteResult as any).mode || '').trim().toLowerCase();
  if (status === 'preview_only' || mode === 'isolated_preview') return false;
  if (status === 'duplicate_exact_file') {
    return ['completed', 'partial'].includes(String((remoteResult as any).batchStatus || '').trim().toLowerCase());
  }
  return (status === 'completed_limited_stage' || status === 'completed')
    && (remoteResult as any).wroteToDb === true;
}

/** Rechecks a server-acknowledged import using GETs only; never re-submits its CSV. */
export async function retryCsvImportVerification(
  remoteResult: unknown,
  verifyRemote: (result: unknown) => boolean | Promise<boolean> = verifyPersistedCsvImport,
): Promise<boolean> {
  if (!isCsvImportVerificationRetryable(remoteResult)) return false;
  return verifyRemote(remoteResult);
}

export async function persistCsvImportAfterLocalSave(options: {
  saveLocal: (remoteResult?: unknown) => boolean | Promise<boolean>;
  requestBody: CsvImportPersistRequest;
  persistRemote?: PersistRemote;
  verifyRemote?: (result: unknown) => boolean | Promise<boolean>;
  onSuccess?: (result: unknown) => void;
  onFailure?: (error: unknown) => void;
}): Promise<{ localSaved: boolean; serverVerified: boolean; backendScheduled: boolean; remoteResult?: any; failure?: Error }> {
  const persistRemote = options.persistRemote ?? persistCsvImportToBackend;
  let remoteResult: any;
  try {
    remoteResult = await persistRemote(options.requestBody);
  } catch (error) {
    options.onFailure?.(error);
    return { localSaved: false, serverVerified: false, backendScheduled: false, failure: error instanceof Error ? error : new Error('CSV server persistence request failed.') };
  }

  const status = String(remoteResult?.status || '').trim().toLowerCase();
  const mode = String(remoteResult?.mode || '').trim().toLowerCase();
  const serverDuplicate = status === 'duplicate_exact_file';
  const serverWriteConfirmed = remoteResult?.wroteToDb === true;

  // A preview-only response from the server must never be treated as successful persistence.
  if (status === 'preview_only' || mode === 'isolated_preview') {
    const error = new Error('Server returned a preview-only response; database persistence was not performed.');
    error.name = 'CsvServerPersistenceError';
    options.onFailure?.(error);
    return { localSaved: false, serverVerified: false, backendScheduled: false, remoteResult, failure: error };
  }

  // A failed batch may retain an audit row while its trade writes were rolled
  // back. Never treat wroteToDb on that envelope as proof of saved trades.
  if (status === 'failed_limited_stage' || status === 'failed_before_pending_batch') {
    const error = new Error(status === 'failed_limited_stage'
      ? 'The server marked this import as failed and rolled back its trade writes. No trades were saved. Check the import status before trying again.'
      : 'The server failed before persistence; no trades were saved. Check the import status before trying again.');
    error.name = 'CsvServerPersistenceError';
    options.onFailure?.(error);
    return { localSaved: false, serverVerified: false, backendScheduled: true, remoteResult, failure: error };
  }

  if (serverDuplicate) {
    if (!['completed', 'partial'].includes(String(remoteResult?.batchStatus || '').trim().toLowerCase())) {
      const error = new Error('The matching CSV batch is not in a verifiable saved state. Check its status before retrying verification.');
      error.name = 'CsvServerPersistenceError';
      options.onFailure?.(error);
      return { localSaved: false, serverVerified: false, backendScheduled: true, remoteResult, failure: error };
    }
    let verified = false;
    try {
      verified = await options.verifyRemote?.(remoteResult) === true;
    } catch {}
    if (verified !== true) {
      const error = new Error('The server recognized this CSV as a duplicate but could not verify its saved trades.');
      error.name = 'CsvServerPersistenceError';
      options.onFailure?.(error);
      return { localSaved: false, serverVerified: false, backendScheduled: true, remoteResult, failure: error };
    }
    options.onSuccess?.(remoteResult);
    return { localSaved: false, serverVerified: true, backendScheduled: true, remoteResult };
  }

  if (!serverWriteConfirmed) {
    const error = new Error(mode === 'disabled_noop' || status === 'writes_disabled'
      ? 'Server CSV persistence is disabled; no trades were saved.'
      : 'Server persistence was not confirmed; no trades were saved.');
    error.name = 'CsvServerPersistenceError';
    options.onFailure?.(error);
    return { localSaved: false, serverVerified: false, backendScheduled: true, remoteResult, failure: error };
  }

  let verified = false;
  try {
    verified = await options.verifyRemote?.(remoteResult) === true;
  } catch {}
  if (!verified) {
    const error = new Error('The server acknowledged the import but the saved trades could not be verified.');
    error.name = 'CsvServerPersistenceError';
    options.onFailure?.(error);
    return { localSaved: false, serverVerified: false, backendScheduled: true, remoteResult, failure: error };
  }

  let localSaved = false;
  try { localSaved = Boolean(await options.saveLocal(remoteResult)); } catch {}

  options.onSuccess?.(remoteResult);
  return { localSaved, serverVerified: true, backendScheduled: true, remoteResult };
}
